import { RotateCcw, Save } from 'lucide-react';
import React from 'react';
import { ActionButton } from './ActionButton';
import { DeleteDialog } from './DeleteDialog';
import { RetryConfirmationDialog } from './RetryConfirmationDialog';
import { ZurueckstellenDialog } from './ZurueckstellenDialog';

interface EditActionsProps {
  isValid: boolean;
  retryDisabledReason?: string | null;
  deleteDisabledReason: string | null;
  hasRunningAbarbeitung: boolean;
  isUserAuthenticated: boolean;
  canSave: boolean;
  canZurueckstellen: boolean;
  istAktuelleAbarbeitung: boolean;
  actionLoading:
    | 'retry'
    | 'delete'
    | 'save'
    | 'lock'
    | 'unlock'
    | 'restore'
    | 'zurueckstellen'
    | null;
  onRetry: () => void;
  onSave: () => void;
  onDelete: () => void;
  onZurueckstellen: (kommentar: string) => void;
  showRetry?: boolean;
  archiveRetry?: boolean;
}

export const EditActions: React.FC<EditActionsProps> = ({
  isValid,
  retryDisabledReason,
  deleteDisabledReason,
  hasRunningAbarbeitung,
  canSave,
  canZurueckstellen,
  istAktuelleAbarbeitung,
  isUserAuthenticated,
  actionLoading,
  onRetry,
  onSave,
  onDelete,
  onZurueckstellen,
  showRetry = true,
  archiveRetry = false,
}) => {
  const isActionDisabled =
    !isValid ||
    actionLoading !== null ||
    !isUserAuthenticated ||
    hasRunningAbarbeitung;
  const isActionDisabledWithoutErrorValidation =
    actionLoading !== null || !isUserAuthenticated;
  const isZurueckstellenLoading = actionLoading === 'zurueckstellen';

  return (
    <>
      {showRetry &&
        (istAktuelleAbarbeitung ? (
          <ActionButton
            onClick={onRetry}
            disabled={isActionDisabled || !!retryDisabledReason}
            loading={actionLoading === 'retry'}
            loadingText="Wird verarbeitet..."
            normalText={
              archiveRetry
                ? 'Archivierung erneut verarbeiten'
                : 'Erneut verarbeiten (Retry)'
            }
            icon={RotateCcw}
            className="bg-blue-500 hover:bg-blue-600 text-white shadow-sm"
          />
        ) : (
          <RetryConfirmationDialog
            onRetry={onRetry}
            disabled={isActionDisabled || !!retryDisabledReason}
            loading={actionLoading === 'retry'}
          />
        ))}
      {archiveRetry && showRetry && (
        <p className="text-sm text-text-secondary">
          Ein bereits hochgeladenes Einzeldokument kann bei einem
          Verbindungsfehler erneut im Archiv erscheinen.
        </p>
      )}
      {showRetry && retryDisabledReason && (
        <p className="w-full text-sm text-text-secondary">
          {retryDisabledReason}
        </p>
      )}
      <ZurueckstellenDialog
        onZurueckstellen={onZurueckstellen}
        disabled={isActionDisabledWithoutErrorValidation || !canZurueckstellen}
        loading={isZurueckstellenLoading}
      />
      <ActionButton
        onClick={onSave}
        disabled={isActionDisabled || !canSave || !istAktuelleAbarbeitung}
        loading={actionLoading === 'save'}
        loadingText="Wird gespeichert..."
        normalText="Speichern"
        icon={Save}
        variant="outline"
      />
      <DeleteDialog
        onDelete={onDelete}
        disabled={actionLoading !== null || deleteDisabledReason !== null}
        disabledReason={deleteDisabledReason}
        loading={actionLoading === 'delete'}
      />
    </>
  );
};
